import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { api, ErroreApi, type Io, type VocePersona } from '../servizi/api.ts'
import { useAree, useCatalogo, usePaesi, useSessione } from '../servizi/sessione.tsx'
import {
  AreaTesto, Bottone, Campo, Etichetta, EtichettaArea, Linguette, Modale, Paginazione, Riquadro,
  Scelta, Spunta, Tabella, type Opzione,
} from '../componenti/base.tsx'
import { Attesa, Avviso, Dato, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'
import { LIMITE, conteggio, usaElenco } from '../componenti/elenco.tsx'
import { IconaIndietro, IconaPiu } from '../componenti/Icone.tsx'
import { CampoModulo, usaModulo } from '../componenti/modulo.tsx'
import { useFuocoSulPrimoErrore } from '../componenti/persona-campi.tsx'
import type { Articolazione } from './Ente.tsx'
import { AvvisoModulo, erroriDellApi, type Etichette } from '../componenti/errori-modulo.tsx'
import { opzioniSedi, sediDellIncarico } from '../servizi/sedi.ts'
import { codiceDaVoce, lingueDalCatalogo, opzioniConAltreVoci, type LinguaCatalogo } from '../servizi/lingue.ts'
import { AIUTO_TELEFONO, emailAccettabile, telefonoAccettabile } from '../servizi/contatti.ts'

/**
 * Registro dei servizi erogati agli enti (D-01).
 *
 * Sostituisce la pagina delle richieste: elenco filtrabile e riepilogo per tipologia, dettaglio
 * con verifica e annullamento, modulo di registrazione e modifica. I permessi letti qui servono
 * solo a non mostrare comandi inutili: il perimetro lo decide l'API, che fuori perimetro risponde
 * 404.
 */

const MEDIAZIONE = 'MIS.MLC'
/** Gestione del catalogo che identifica i servizi resi agli enti. */
const GESTIONE_ENTI = 'erogazione_a_ente'
/** Filtri che passano dall'indirizzo della pagina alla rotta: un elenco filtrato si puo' condividere. */
const CHIAVI_FILTRO = ['ente', 'progetto', 'sede', 'area', 'tipologia', 'dal', 'al', 'da_verificare', 'stato'] as const

export type Ente = { id: string; nome: string; sigla: string | null }
type Progetto = { id: string; titolo: string; codice_cidis: string | null }
/**
 * Progetti delle tendine: GET /progetti/selezionabili segue il perimetro della registrazione. L'elenco
 * dei progetti mostra a un incarico di sede solo i progetti collegati alla sede, e la tendina resterebbe
 * vuota per chi registra servizi su progetti non ancora collegati.
 */
const PROGETTI_SELEZIONABILI = '/progetti/selezionabili?limite=500'
const PERMESSI_PROGETTI = ['progetto.leggi', 'servizio_erogato.registra', 'servizio_erogato.modifica']
type Mediatore = { id: string; nome: string; cognome: string }
type Lingua = LinguaCatalogo
type Referente = { id: string; nome: string; cognome: string; attivo: boolean; rappresentante_legale?: boolean }
type NuovoReferente = { nome: string; cognome: string; ruolo: string; telefono: string; email: string }
const REFERENTE_VUOTO: NuovoReferente = { nome: '', cognome: '', ruolo: '', telefono: '', email: '' }
type PersonaScelta = { id: string; etichetta: string }

type RigaRegistro = {
  // N-2: senza ente la riga e' un servizio che CIDIS ha reso a se stessa. `ente_etichetta` arriva
  // dall'API gia' pronta («CIDIS — nome sede», oppure «Sedi CIDIS (interno)» se manca anche quella),
  // cosi' elenco, dettaglio e riepiloghi chiamano quelle righe allo stesso modo.
  id: string; data: string; stakeholder_id: string | null; ente: string | null; ente_etichetta: string
  ente_sigla: string | null
  tipologia_codice: string; tipologia: string; area_codice: string
  progetto: string | null; progetto_codice: string | null; sede: string | null; ore: number | null
  mediatore_nome: string | null; mediatore_cognome: string | null
  da_verificare: boolean; con_persona: boolean; annullato: boolean
}

type VoceRiepilogo = {
  tipologia_codice: string; tipologia: string; area_codice: string
  servizi: number; ore: number; enti: number; da_verificare: number
}

type Dettaglio = {
  id: string; data: string; stakeholder_id: string | null; ente: string | null; ente_etichetta: string
  ente_sigla: string | null
  referente_id: string | null; referente_nome: string | null; referente_cognome: string | null
  articolazione_id: string | null; articolazione: string | null
  tipologia_codice: string; tipologia: string; area_codice: string; tipologia_storica: boolean
  sottotipologia_codice: string | null; sottotipologia: string | null
  tema_codice: string | null; tema: string | null
  progetto_id: string | null; progetto: string | null; progetto_codice: string | null
  sede_id: string | null; sede: string | null; ore: number | null
  modalita: string | null; modalita_nome: string | null; luogo: string | null
  mediatore_id: string | null; mediatore_nome: string | null; mediatore_cognome: string | null
  lingua: string | null; lingua_nome: string | null; paese: string | null; paese_nome: string | null
  /** Nullo anche quando la persona e' collegata ma fuori dal perimetro: vale `con_persona`. */
  persona_id: string | null; persona: { id: string; numero_scheda: number; etichetta: string } | null
  con_persona: boolean; persona_visibile: boolean
  /** Comandi che valgono su questa riga per chi legge (459): assenti nelle risposte precedenti. */
  puo_modificare?: boolean; puo_annullare?: boolean
  soddisfazione: string | null; soddisfazione_nome: string | null; attestato: boolean | null
  dati: Record<string, unknown>; descrizione: string | null
  /** Risposte del modulo della tipologia con le etichette della versione registrata. */
  risposte?: { campo: string; etichetta: string; testo: string }[]
  da_verificare: boolean; motivo_verifica: string | null; verificato_il: string | null
  verificato_da_nome: string | null; verificato_da_cognome: string | null
  operatore_nome: string | null; operatore_cognome: string | null
  annullato_il: string | null; motivo_annullamento: string | null
  annullato_da_nome: string | null; annullato_da_cognome: string | null
  creato_il: string; aggiornato_il: string | null
  richiesta_id: string | null; erogazione_id: string | null; richiesta_legacy_id: number | null
  erogazione_legacy_tabella: string | null; erogazione_legacy_id: number | null
  lingua_originale: string | null
}

// --- aiuti -------------------------------------------------------------------------------------

/** Le date del registro sono giorni senza ora: si scrivono senza passare dal fuso del browser. */
const dataBreve = (v: string | null | undefined) => (v ? v.slice(0, 10).split('-').reverse().join('/') : '—')
const momento = (v: string | null | undefined) =>
  (v ? new Date(v).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—')
const nomeCompleto = (nome: string | null, cognome: string | null) =>
  [nome, cognome].filter(Boolean).join(' ') || null
const oggi = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' })
/** Ore con la virgola decimale: la rotta le restituisce come numero JavaScript. */
const ore = (v: number | null | undefined) =>
  (v == null ? null : Number(v).toLocaleString('it-IT', { maximumFractionDigits: 2 }))
/** Testo al posto di un motivo che l'API non restituisce fuori dal perimetro della scheda. */
const MOTIVO_RISERVATO = 'riservato: il servizio riguarda un beneficiario che non rientra nei tuoi incarichi.'

function messaggioDi(e: unknown, fuoriPerimetro: string): string {
  if (!(e instanceof ErroreApi)) return 'Non è andata a buon fine: controlla la connessione e riprova.'
  return e.stato === 404 ? fuoriPerimetro : e.message
}

/** Lettura di un elenco di appoggio per le tendine; senza permesso o in errore resta vuoto. */
function useVoci<T>(percorso: string | null): T[] {
  const [voci, setVoci] = useState<T[]>([])
  useEffect(() => {
    if (!percorso) { setVoci([]); return }
    let valido = true
    api.get<T[]>(percorso)
      .then((r) => { if (valido) setVoci(r) })
      .catch(() => { if (valido) setVoci([]) })
    return () => { valido = false }
  }, [percorso])
  return voci
}

/** Aggiunge alle opzioni il valore gia' registrato quando non compare fra quelle attive. */
function conValore(opzioni: Opzione[], valore: string | null | undefined, etichetta: string | null | undefined) {
  if (!valore || opzioni.some((o) => o.valore === valore)) return opzioni
  return [{ valore, etichetta: etichetta ?? valore }, ...opzioni]
}

function StatoServizio({ annullato, daVerificare }: { annullato: boolean; daVerificare: boolean }) {
  if (annullato) return <Etichetta tono="critico">annullato</Etichetta>
  if (daVerificare) return <Etichetta tono="attenzione">da verificare</Etichetta>
  return <Etichetta tono="positivo">registrato</Etichetta>
}

// --- ricerche guidate ---------------------------------------------------------------------------

/** Esito di una ricerca guidata: risultati, attesa della risposta, errore. */
export type Ricerca<T> = { trovati: T[]; inCorso: boolean; errore: boolean; conclusa: boolean }

/**
 * Risultati di una ricerca guidata. La lettura parte con almeno due caratteri e dopo una pausa
 * nella digitazione; una risposta arrivata dopo un termine nuovo si scarta. `percorso` e `adatta`
 * devono avere identita' stabile (funzioni di modulo), altrimenti la lettura ripartirebbe a ogni
 * render.
 *
 * `inCorso` vale dalla digitazione alla risposta, `conclusa` solo dopo una risposta per il termine
 * scritto: prima l'aiuto annunciava «nessun risultato» durante la pausa e l'attesa, e anche quando
 * la lettura falliva.
 */
export function useTrovati<R, T>(testo: string, percorso: (termine: string) => string,
  adatta: (risposta: R) => T[], pausa: number, minimo = 2): Ricerca<T> {
  const [esito, setEsito] = useState<{ indirizzo: string | null; trovati: T[]; errore: boolean }>(
    { indirizzo: null, trovati: [], errore: false })
  const termine = testo.trim()
  // `minimo` a zero significa elenco sempre caricato, anche senza testo: serve alle tendine di
  // poche decine di voci, dove aspettare due lettere sembra un guasto (Osservazioni 3, punto 3).
  const indirizzo = termine.length >= minimo ? percorso(termine) : null
  useEffect(() => {
    if (!indirizzo) return
    let valido = true
    const attesa = setTimeout(() => {
      api.get<R>(indirizzo)
        .then((r) => { if (valido) setEsito({ indirizzo, trovati: adatta(r), errore: false }) })
        .catch(() => { if (valido) setEsito({ indirizzo, trovati: [], errore: true }) })
    }, pausa)
    return () => { valido = false; clearTimeout(attesa) }
  }, [indirizzo, adatta, pausa])
  const conclusa = !!indirizzo && esito.indirizzo === indirizzo
  return {
    trovati: indirizzo ? esito.trovati : [],
    inCorso: !!indirizzo && !conclusa,
    errore: conclusa && esito.errore,
    conclusa,
  }
}

/** Aiuto di un campo di ricerca guidata: attesa, errore, nessun risultato, altrimenti `altrimenti`. */
export function aiutoRicerca<T>(r: Ricerca<T>, nessuno: string, altrimenti?: ReactNode): ReactNode {
  if (r.inCorso) return 'Ricerca in corso…'
  if (r.errore) return 'Ricerca non riuscita.'
  if (r.conclusa && r.trovati.length === 0) return nessuno
  return altrimenti
}

/** Risposta che e' gia' l'elenco dei risultati. */
export function comeArrivano<T>(risposta: T[]): T[] { return risposta }

/** Schede collegabili: le mascherate restano fuori. */
function personeCollegabili(risposta: { risultati: VocePersona[] }): PersonaScelta[] {
  return risposta.risultati.filter((p) => !p.mascherata)
    .map((p) => ({ id: p.id, etichetta: `${p.etichetta} · scheda n. ${p.numero_scheda}` }))
}

/**
 * Scelta di un ente per nome. Gli enti sono centinaia: una tendina completa sarebbe illeggibile,
 * quindi si cerca per nome e si sceglie fra i risultati.
 */
export function SceltaEnte({ etichetta, valore, cambia, obbligatorio = false, vuota, errore, perimetroPersone = false }: {
  etichetta: string; valore: Ente | null; cambia: (e: Ente | null) => void
  obbligatorio?: boolean; vuota: string; errore?: string | null
  /**
   * Cerca fra gli enti selezionabili dalle schede e dagli interventi (GET /persone/enti-selezionabili)
   * invece che nell'anagrafica degli enti, che chiede ente.leggi.
   */
  perimetroPersone?: boolean
}) {
  const [testo, setTesto] = useState('')
  const ricerca = useTrovati<Ente[], Ente>(testo, perimetroPersone ? percorsoEntiSelezionabili : percorsoEnti,
    comeArrivano, 300)
  const { trovati } = ricerca

  const candidati = valore && !trovati.some((e) => e.id === valore.id) ? [valore, ...trovati] : trovati
  return (
    <div className="rete-ricerca rete-ricerca-piena">
      <Campo etichetta={`Cerca ${etichetta.toLowerCase()}`} tipo="search" valore={testo} cambia={setTesto}
             segnaposto="Almeno due lettere del nome o della sigla" />
      <Scelta etichetta={etichetta} obbligatorio={obbligatorio} vuota={vuota} errore={errore}
              valore={valore?.id ?? ''}
              opzioni={candidati.map((e) => ({ valore: e.id, etichetta: e.sigla ? `${e.sigla} — ${e.nome}` : e.nome }))}
              cambia={(id) => cambia(candidati.find((e) => e.id === id) ?? null)}
              aiuto={aiutoRicerca(ricerca, 'Nessun ente trovato con questo nome.')} />
    </div>
  )
}

const percorsoEnti = (termine: string) => `/enti?cerca=${encodeURIComponent(termine)}&limite=20`
const percorsoEntiSelezionabili = (termine: string) =>
  `/persone/enti-selezionabili?cerca=${encodeURIComponent(termine)}&limite=20`

/**
 * Scelta del mediatore per nome. L'albo supera la finestra massima di un elenco (200 righe): una
 * tendina caricata una volta sola perderebbe gli ultimi cognomi in ordine alfabetico, quindi si
 * cerca e si sceglie fra i risultati. Il mediatore gia' scelto resta fra i candidati.
 */
function SceltaMediatore({ valore, cambia, obbligatorio, errore }: {
  valore: Mediatore | null; cambia: (m: Mediatore | null) => void; obbligatorio: boolean; errore?: string | null
}) {
  const [testo, setTesto] = useState('')
  const ricerca = useTrovati<Mediatore[], Mediatore>(testo, percorsoMediatori, comeArrivano, 300)
  const { trovati } = ricerca

  const candidati = valore && !trovati.some((m) => m.id === valore.id) ? [valore, ...trovati] : trovati
  return (
    <div className="rete-ricerca rete-ricerca-piena">
      <Campo etichetta="Cerca il mediatore" tipo="search" valore={testo} cambia={setTesto}
             segnaposto="Almeno due lettere del nome o del cognome" />
      <Scelta etichetta="Mediatore" obbligatorio={obbligatorio} vuota="— nessuno —" valore={valore?.id ?? ''}
              errore={errore}
              opzioni={candidati.map((m) => ({ valore: m.id, etichetta: `${m.cognome} ${m.nome}` }))}
              cambia={(id) => cambia(candidati.find((m) => m.id === id) ?? null)}
              aiuto={aiutoRicerca(ricerca, 'Nessun mediatore attivo trovato con questo nome.',
                obbligatorio ? 'Obbligatorio per la mediazione linguistico-culturale.' : undefined)} />
    </div>
  )
}

const percorsoMediatori = (termine: string) => `/mediatori?cerca=${encodeURIComponent(termine)}&limite=20`
const percorsoPersone = (termine: string) => `/persone?cerca=${encodeURIComponent(termine)}&limite=10`

/** Scelta del beneficiario, facoltativa. Le schede mascherate non si possono collegare. */
function SceltaPersona({ valore, cambia, errore }: {
  valore: PersonaScelta | null; cambia: (p: PersonaScelta | null) => void; errore?: string | null
}) {
  const [testo, setTesto] = useState('')
  const ricerca = useTrovati<{ risultati: VocePersona[] }, PersonaScelta>(testo, percorsoPersone,
    personeCollegabili, 400)
  const trovate = ricerca.trovati

  const candidate = valore && !trovate.some((p) => p.id === valore.id) ? [valore, ...trovate] : trovate
  return (
    <div className="rete-ricerca rete-ricerca-piena">
      <Campo etichetta="Cerca il beneficiario" tipo="search" valore={testo} cambia={setTesto}
             segnaposto="Nome, cognome o numero di scheda" />
      <Scelta etichetta="Beneficiario" vuota="— nessuno —" valore={valore?.id ?? ''} errore={errore}
              opzioni={candidate.map((p) => ({ valore: p.id, etichetta: p.etichetta }))}
              cambia={(id) => cambia(candidate.find((p) => p.id === id) ?? null)}
              aiuto={aiutoRicerca(ricerca, 'Nessuna scheda trovata con questo nome.',
                'Facoltativo: solo se il servizio riguarda una persona con scheda.')} />
    </div>
  )
}

// --- elenco ---------------------------------------------------------------------------------------

export function Registro() {
  const { puo, sedi, tassonomia } = useSessione()
  const aree = useAree()
  const naviga = useNavigate()
  const [parametri, setParametri] = useSearchParams()
  const [vista, setVista] = useState<'elenco' | 'riepilogo'>('elenco')
  const [registrazione, setRegistrazione] = useState(false)
  const [ente, setEnte] = useState<Ente | null>(null)
  const [riepilogo, setRiepilogo] = useState<VoceRiepilogo[] | null>(null)
  const [erroreRiepilogo, setErroreRiepilogo] = useState<string | null>(null)

  const filtro = (chiave: (typeof CHIAVI_FILTRO)[number]) => parametri.get(chiave) ?? ''
  function aggiorna(chiave: (typeof CHIAVI_FILTRO)[number], valore: string) {
    const nuovi = new URLSearchParams(parametri)
    if (valore) nuovi.set(chiave, valore); else nuovi.delete(chiave)
    setParametri(nuovi, { replace: true })
  }

  const query = useMemo(() => {
    const q = new URLSearchParams()
    for (const chiave of CHIAVI_FILTRO) {
      const valore = parametri.get(chiave)
      if (valore) q.set(chiave, valore)
    }
    return q.toString()
  }, [parametri])
  const { dati, errore, finestra } = usaElenco<RigaRegistro>(`/servizi-erogati${query ? `?${query}` : ''}`)

  // Il filtro per ente arriva dall'indirizzo come identificativo: il nome si recupera una volta.
  const enteFiltrato = parametri.get('ente')
  useEffect(() => {
    if (!enteFiltrato) { setEnte(null); return }
    if (ente?.id === enteFiltrato) return
    api.get<Ente>(`/enti/${encodeURIComponent(enteFiltrato)}`)
      .then((e) => setEnte({ id: e.id, nome: e.nome, sigla: e.sigla }))
      .catch(() => setEnte({ id: enteFiltrato, nome: 'Ente selezionato', sigla: null }))
  }, [enteFiltrato]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (vista !== 'riepilogo') return
    setRiepilogo(null)
    setErroreRiepilogo(null)
    api.get<VoceRiepilogo[]>(`/servizi-erogati/riepilogo${query ? `?${query}` : ''}`)
      .then(setRiepilogo)
      .catch((e) => setErroreRiepilogo(messaggioDi(e, 'Il registro non è disponibile con i tuoi incarichi.')))
  }, [vista, query])

  const progetti = useVoci<Progetto>(PERMESSI_PROGETTI.some((p) => puo(p)) ? PROGETTI_SELEZIONABILI : null)
  const tipologie = (tassonomia?.tipologie ?? []).filter((t) => t.gestione === GESTIONE_ENTI)
  const conFiltri = CHIAVI_FILTRO.some((chiave) => parametri.get(chiave))

  return (
    <>
      <Intestazione
        titolo="Registro dei servizi erogati"
        sottotitolo={conteggio(finestra.totale, 'servizio', 'servizi')}
        azioni={puo('servizio_erogato.registra')
          ? <Bottone variante="primario" icona={<IconaPiu />} onClick={() => setRegistrazione(true)}>
              Registra un servizio
            </Bottone>
          : undefined}
      />

      <Riquadro titolo="Filtri" azioni={conFiltri
        ? <Bottone variante="discreto" dimensione="piccolo"
                   onClick={() => setParametri(new URLSearchParams(), { replace: true })}>Azzera i filtri</Bottone>
        : undefined}>
        <div className="filtri-report">
          <SceltaEnte etichetta="Ente" vuota="Tutti gli enti" valore={ente}
                      cambia={(e) => { setEnte(e); aggiorna('ente', e?.id ?? '') }} />
          <Scelta etichetta="Progetto" vuota="Tutti" valore={filtro('progetto')}
                  cambia={(v) => aggiorna('progetto', v)}
                  opzioni={progetti.map((p) => ({
                    valore: p.id, etichetta: p.codice_cidis ? `${p.codice_cidis} · ${p.titolo}` : p.titolo }))} />
          <Scelta etichetta="Sede" vuota="Tutte" valore={filtro('sede')} cambia={(v) => aggiorna('sede', v)}
                  opzioni={opzioniSedi(sedi)} />
          {/* D-05: la tendina Area filtra gli elenchi. La tipologia scelta resta solo se e' dell'area. */}
          <Scelta etichetta="Area" vuota="Tutte" valore={filtro('area')}
                  cambia={(v) => {
                    const nuovi = new URLSearchParams(parametri)
                    if (v) nuovi.set('area', v); else nuovi.delete('area')
                    const scelta = tipologie.find((t) => t.codice === parametri.get('tipologia'))
                    if (v && scelta && scelta.area_codice !== v) nuovi.delete('tipologia')
                    setParametri(nuovi, { replace: true })
                  }}
                  opzioni={aree.aree.filter((a) => tipologie.some((t) => t.area_codice === a.codice))
                    .map((a) => ({ valore: a.codice, etichetta: a.nome }))} />
          <Scelta etichetta="Tipologia" vuota="Tutte" valore={filtro('tipologia')}
                  cambia={(v) => aggiorna('tipologia', v)}
                  opzioni={tipologie.filter((t) => !filtro('area') || t.area_codice === filtro('area')).map((t) => ({
                    valore: t.codice, etichetta: t.storica ? `${t.nome} (storica)` : t.nome }))} />
          <Campo etichetta="Dal" tipo="date" valore={filtro('dal')} cambia={(v) => aggiorna('dal', v)}
                 max={filtro('al') || undefined} />
          <Campo etichetta="Al" tipo="date" valore={filtro('al')} cambia={(v) => aggiorna('al', v)}
                 min={filtro('dal') || undefined} />
          <Scelta etichetta="Verifica" vuota="Tutti" valore={filtro('da_verificare')}
                  cambia={(v) => aggiorna('da_verificare', v)}
                  opzioni={[
                    { valore: 'true', etichetta: 'Da verificare' },
                    // `false` comprende le righe mai segnalate, non solo le verifiche concluse.
                    { valore: 'false', etichetta: 'Non da verificare' },
                  ]} />
          <Scelta etichetta="Stato" valore={filtro('stato') || 'validi'}
                  cambia={(v) => aggiorna('stato', v === 'validi' ? '' : v)}
                  opzioni={[
                    { valore: 'validi', etichetta: 'Validi' },
                    { valore: 'annullati', etichetta: 'Annullati' },
                    { valore: 'tutti', etichetta: 'Tutti' },
                  ]} />
        </div>
      </Riquadro>

      <Linguette etichetta="Vista del registro" attiva={vista} cambia={setVista}
                 voci={[{ valore: 'elenco', etichetta: 'Elenco' }, { valore: 'riepilogo', etichetta: 'Riepilogo per tipologia' }]} />

      {vista === 'elenco' && (
        <>
          {errore && <Avviso tipo="errore">{errore}</Avviso>}
          {!errore && !dati && <Attesa />}
          {dati && (
            <Riquadro denso>
              <Tabella intestazioni={['Data', 'Ente', 'Tipologia', 'Progetto', 'Sede', 'Ore', 'Stato']}
                       vuoto={<Vuoto testo="Nessun servizio con questi filtri fra quelli che puoi vedere." />}>
                {dati.map((r) => (
                  <tr key={r.id}>
                    <td data-etichetta="Data">
                      <Link to={`/registro/${r.id}`} className="voce-principale">{dataBreve(r.data)}</Link>
                    </td>
                    <td data-etichetta="Ente">
                      {r.ente ? `${r.ente_sigla ? `${r.ente_sigla} — ` : ''}${r.ente}` : r.ente_etichetta}
                    </td>
                    <td data-etichetta="Tipologia">
                      <span className="rete-tipologia">
                        <EtichettaArea codice={r.area_codice} colore={aree.coloreDi(r.area_codice)} />
                        {r.tipologia}
                      </span>
                    </td>
                    <td data-etichetta="Progetto">
                      {r.progetto ? (r.progetto_codice ?? r.progetto) : <span className="tenue">—</span>}
                    </td>
                    <td data-etichetta="Sede">{r.sede ?? <span className="tenue">—</span>}</td>
                    <td data-etichetta="Ore" className="mono">{ore(r.ore) ?? '—'}</td>
                    <td data-etichetta="Stato">
                      <StatoServizio annullato={r.annullato} daVerificare={r.da_verificare} />
                    </td>
                  </tr>
                ))}
              </Tabella>
              <Paginazione salta={finestra.salta} limite={LIMITE} totale={finestra.totale}
                           ricevute={dati.length} cambia={finestra.cambia} />
            </Riquadro>
          )}
        </>
      )}

      {vista === 'riepilogo' && (
        <>
          {erroreRiepilogo && <Avviso tipo="errore">{erroreRiepilogo}</Avviso>}
          {!erroreRiepilogo && !riepilogo && <Attesa />}
          {riepilogo && (
            <Riquadro denso>
              <Tabella intestazioni={['Tipologia', 'Servizi', 'Ore', 'Enti', 'Da verificare']}
                       vuoto={<Vuoto testo="Nessun servizio con questi filtri fra quelli che puoi vedere." />}>
                {riepilogo.map((v) => (
                  <tr key={v.tipologia_codice}>
                    <td data-etichetta="Tipologia">
                      <span className="rete-tipologia">
                        <EtichettaArea codice={v.area_codice} colore={aree.coloreDi(v.area_codice)} />
                        {v.tipologia}
                      </span>
                    </td>
                    <td data-etichetta="Servizi" className="mono">{v.servizi}</td>
                    <td data-etichetta="Ore" className="mono">{ore(v.ore)}</td>
                    <td data-etichetta="Enti" className="mono">{v.enti}</td>
                    <td data-etichetta="Da verificare" className="mono">{v.da_verificare}</td>
                  </tr>
                ))}
              </Tabella>
              <p className="nota-modulo">
                Conteggi dei servizi che puoi vedere, con i filtri scelti. Le ore sommano i valori
                registrati, compresi quelli ancora da verificare.
              </p>
            </Riquadro>
          )}
        </>
      )}

      {registrazione && (
        <Modale titolo="Registra un servizio erogato" larga chiudi={() => setRegistrazione(false)}>
          <ModuloServizio annulla={() => setRegistrazione(false)}
                          salvato={(id) => { setRegistrazione(false); naviga(`/registro/${id}`) }} />
        </Modale>
      )}
    </>
  )
}

// --- dettaglio ------------------------------------------------------------------------------------

export function ServizioErogato() {
  const { id } = useParams()
  const { puo } = useSessione()
  const aree = useAree()
  const [dettaglio, setDettaglio] = useState<Dettaglio | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [erroreAzione, setErroreAzione] = useState<string | null>(null)
  const [modifica, setModifica] = useState(false)
  const [annullamento, setAnnullamento] = useState(false)
  const [verificaInCorso, setVerificaInCorso] = useState(false)

  const carica = useCallback(() => {
    setErrore(null)
    api.get<Dettaglio>(`/servizi-erogati/${id}`)
      .then(setDettaglio)
      .catch((e) => setErrore(messaggioDi(e, 'Servizio non trovato, oppure non rientra nei tuoi incarichi.')))
  }, [id])
  useEffect(carica, [carica])

  async function concludiVerifica() {
    setErroreAzione(null)
    setVerificaInCorso(true)
    try {
      await api.patch(`/servizi-erogati/${id}`, { da_verificare: false })
      carica()
    } catch (e) {
      setErroreAzione(messaggioDi(e, 'Con i tuoi incarichi non puoi farlo.'))
    } finally {
      setVerificaInCorso(false)
    }
  }

  const d = dettaglio
  const attivo = !!d && !d.annullato_il
  const risposte = d?.risposte ?? []
  const modificabile = puo('servizio_erogato.modifica') && d?.puo_modificare !== false
  const annullabile = puo('servizio_erogato.annulla') && d?.puo_annullare !== false

  return (
    <>
      <Intestazione
        titolo="Servizio erogato"
        sottotitolo={d ? `${d.ente ?? d.ente_etichetta} · ${dataBreve(d.data)}` : undefined}
        azioni={<>
          <Link className="bottone bottone-discreto" to="/registro"><IconaIndietro /> Registro</Link>
          {attivo && modificabile && (
            <Bottone variante="secondario" onClick={() => setModifica(true)}>Modifica</Bottone>
          )}
          {attivo && annullabile && (
            <Bottone variante="critico" onClick={() => setAnnullamento(true)}>Annulla il servizio</Bottone>
          )}
        </>}
      />

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!errore && !d && <Attesa />}
      {erroreAzione && <Avviso tipo="errore">{erroreAzione}</Avviso>}

      {d && (
        <>
          {d.annullato_il && (
            <Avviso tipo="errore">
              <strong>Servizio annullato</strong> il {momento(d.annullato_il)}
              {nomeCompleto(d.annullato_da_nome, d.annullato_da_cognome)
                ? ` da ${nomeCompleto(d.annullato_da_nome, d.annullato_da_cognome)}` : ''}.
              {' '}Motivo: {d.motivo_annullamento ?? MOTIVO_RISERVATO}
            </Avviso>
          )}
          {attivo && d.da_verificare && (
            <Avviso tipo="attenzione">
              <strong>Da verificare:</strong> {d.motivo_verifica ?? `motivo ${MOTIVO_RISERVATO}`}
              {modificabile && (
                <div className="rete-azioni-avviso">
                  <Bottone variante="secondario" dimensione="piccolo" inCorso={verificaInCorso}
                           onClick={() => void concludiVerifica()}>
                    Conferma la verifica
                  </Bottone>
                </div>
              )}
            </Avviso>
          )}

          <div className="rete-dettaglio">
            <Riquadro titolo="Servizio">
              <dl className="rete-dati">
                <Dato nome="Ente">
                  {d.ente ? `${d.ente_sigla ? `${d.ente_sigla} — ` : ''}${d.ente}` : d.ente_etichetta}
                </Dato>
                <Dato nome="Referente">{nomeCompleto(d.referente_nome, d.referente_cognome)}</Dato>
                <Dato nome="Dipartimento o filiale">{d.articolazione}</Dato>
                <Dato nome="Tipologia">
                  <span className="rete-tipologia">
                    <EtichettaArea codice={d.area_codice} colore={aree.coloreDi(d.area_codice)} />
                    {d.tipologia}
                    {d.tipologia_storica && <Etichetta>storica</Etichetta>}
                  </span>
                </Dato>
                {d.sottotipologia && <Dato nome="Sottotipologia">{d.sottotipologia}</Dato>}
                <Dato nome="Tema">{d.tema}</Dato>
                <Dato nome="Data">{dataBreve(d.data)}</Dato>
                <Dato nome={d.tipologia_codice === MEDIAZIONE ? 'Ore di mediazione' : 'Ore'}>{ore(d.ore)}</Dato>
                <Dato nome="Modalità">{d.modalita_nome ?? d.modalita}</Dato>
                <Dato nome="Luogo">{d.luogo}</Dato>
                <Dato nome="Sede">{d.sede}</Dato>
                <Dato nome="Progetto">
                  {d.progetto ? `${d.progetto_codice ? `${d.progetto_codice} · ` : ''}${d.progetto}` : null}
                </Dato>
              </dl>
            </Riquadro>

            <Riquadro titolo="Mediazione e destinatario">
              <dl className="rete-dati">
                <Dato nome="Mediatore">{nomeCompleto(d.mediatore_nome, d.mediatore_cognome)}</Dato>
                <Dato nome="Lingua">{d.lingua_nome ?? d.lingua}</Dato>
                <Dato nome="Paese">{d.paese_nome ?? d.paese}</Dato>
                <Dato nome="Beneficiario">
                  {d.persona
                    ? <Link to={`/persone/${d.persona.id}`}>{d.persona.etichetta} · scheda n. {d.persona.numero_scheda}</Link>
                    : d.con_persona
                      ? <span className="tenue">collegato, ma non rientra nei tuoi incarichi</span>
                      : null}
                </Dato>
                <Dato nome="Soddisfazione dell’ente">{d.soddisfazione_nome ?? d.soddisfazione}</Dato>
                <Dato nome="Attestato">
                  {d.attestato === null ? null : d.attestato ? 'rilasciato' : 'non rilasciato'}
                </Dato>
              </dl>
            </Riquadro>
          </div>

          <Riquadro titolo="Descrizione">
            {d.descrizione
              ? <p className="testo-libero">{d.descrizione}</p>
              : <p className="tenue">
                  {d.con_persona && !d.persona_visibile
                    ? 'La descrizione riguarda un beneficiario che non rientra nei tuoi incarichi: non si può leggere.'
                    : 'Nessuna descrizione.'}
                </p>}
          </Riquadro>

          {/* Le risposte arrivano con le etichette della versione del modulo con cui sono state
              registrate, come negli interventi: a video non compaiono i codici dei campi e delle voci. */}
          {risposte.length > 0 && (
            <Riquadro titolo="Campi della tipologia">
              <dl className="rete-dati">
                {risposte.map((r) => <Dato key={r.campo} nome={r.etichetta}>{r.testo}</Dato>)}
              </dl>
            </Riquadro>
          )}

          <Riquadro titolo="Registrazione e provenienza">
            <dl className="rete-dati">
              <Dato nome="Operatore">{nomeCompleto(d.operatore_nome, d.operatore_cognome)}</Dato>
              <Dato nome="Registrato il">{momento(d.creato_il)}</Dato>
              {d.aggiornato_il && <Dato nome="Ultima modifica">{momento(d.aggiornato_il)}</Dato>}
              {d.verificato_il && (
                <Dato nome="Verifica conclusa">
                  {momento(d.verificato_il)}
                  {nomeCompleto(d.verificato_da_nome, d.verificato_da_cognome)
                    ? ` · ${nomeCompleto(d.verificato_da_nome, d.verificato_da_cognome)}` : ''}
                  {d.motivo_verifica ? ` · ${d.motivo_verifica}` : ''}
                </Dato>
              )}
              {d.richiesta_legacy_id !== null && (
                <Dato nome="Richiesta nel vecchio archivio">n. {d.richiesta_legacy_id}</Dato>
              )}
              {d.erogazione_legacy_id !== null && (
                <Dato nome="Erogazione nel vecchio archivio">
                  {d.erogazione_legacy_tabella} n. {d.erogazione_legacy_id}
                </Dato>
              )}
              {d.lingua_originale && <Dato nome="Lingua come scritta nel vecchio archivio">{d.lingua_originale}</Dato>}
              {d.richiesta_id && d.richiesta_legacy_id === null && (
                <Dato nome="Provenienza">richiesta dell’ente registrata prima della dismissione delle richieste</Dato>
              )}
            </dl>
          </Riquadro>
        </>
      )}

      {modifica && d && (
        <Modale titolo="Modifica il servizio" larga chiudi={() => setModifica(false)}>
          <ModuloServizio iniziale={d} annulla={() => setModifica(false)}
                          salvato={() => { setModifica(false); carica() }} />
        </Modale>
      )}
      {annullamento && id && (
        <ModaleAnnullamento id={id} chiudi={() => setAnnullamento(false)}
                            annullato={() => { setAnnullamento(false); carica() }} />
      )}
    </>
  )
}

function ModaleAnnullamento({ id, chiudi, annullato }: { id: string; chiudi: () => void; annullato: () => void }) {
  const [motivo, setMotivo] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function conferma() {
    setErrore(null)
    setInCorso(true)
    try {
      await api.post(`/servizi-erogati/${id}/annulla`, { motivo: motivo.trim() })
      annullato()
    } catch (e) {
      setErrore(messaggioDi(e, 'Con i tuoi incarichi non puoi farlo.'))
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo="Annulla il servizio" chiudi={chiudi} azioni={<>
      <Bottone variante="discreto" onClick={chiudi}>Indietro</Bottone>
      <Bottone variante="critico" inCorso={inCorso} disabled={motivo.trim().length < 5}
               onClick={() => void conferma()}>Annulla il servizio</Bottone>
    </>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p>Il servizio resta nel registro segnato come annullato ed esce dai conteggi dei servizi validi.</p>
      <AreaTesto etichetta="Motivo" obbligatorio righe={3} maxLength={500} valore={motivo} cambia={setMotivo}
                 aiuto="Almeno cinque caratteri. Il motivo resta visibile nel dettaglio." />
    </Modale>
  )
}

// --- registrazione e modifica -----------------------------------------------------------------

type ValoriModulo = {
  referente_id: string; articolazione_id: string; progetto_id: string; sede_id: string; tipologia_codice: string
  sottotipologia_codice: string; tema_codice: string; data: string; ore: string; modalita: string
  luogo: string; lingua: string; paese: string; descrizione: string
  soddisfazione: string; attestato: '' | 'si' | 'no'; da_verificare: boolean; motivo_verifica: string
}

/**
 * Voci scelte con una ricerca guidata. `persona` indefinita vale «collegata ma fuori dal
 * perimetro»: l'identificativo non si conosce e il campo non parte.
 */
type Scelte = { ente: Ente | null; mediatore: Mediatore | null; persona: PersonaScelta | null | undefined }

function valoriIniziali(d?: Dettaglio): ValoriModulo {
  return {
    referente_id: d?.referente_id ?? '', articolazione_id: d?.articolazione_id ?? '',
    progetto_id: d?.progetto_id ?? '', sede_id: d?.sede_id ?? '',
    tipologia_codice: d?.tipologia_codice ?? '', sottotipologia_codice: d?.sottotipologia_codice ?? '',
    tema_codice: d?.tema_codice ?? '', data: d?.data ?? oggi(), ore: d?.ore == null ? '' : String(d.ore),
    modalita: d?.modalita ?? '', luogo: d?.luogo ?? '',
    lingua: d?.lingua ?? '', paese: d?.paese ?? '', descrizione: d?.descrizione ?? '',
    soddisfazione: d?.soddisfazione ?? '',
    attestato: d?.attestato == null ? '' : d.attestato ? 'si' : 'no',
    da_verificare: d?.da_verificare ?? false, motivo_verifica: d?.motivo_verifica ?? '',
  }
}

/**
 * Sede proposta per un servizio nuovo (segnalazione del 19/10/2026: il campo obbligatorio partiva
 * vuoto). Con un solo incarico di sede che registra, quella; altrimenti la sede principale
 * dell'anagrafica dell'operatore, fra quelle dei suoi incarichi se ne ha. Resta modificabile.
 */
function sedeProposta(io: Io | null): string {
  if (!io) return ''
  const incarichi = io.incarichi
    .filter((i) => i.ambito.tipo === 'sede' && i.ambito.id && i.permessi.includes('servizio_erogato.registra'))
  const sediIncarichi = [...new Set(incarichi.map((i) => i.ambito.id as string))]
  if (sediIncarichi.length === 1) return sediIncarichi[0]
  // La sede principale dell'anagrafica vale se un incarico la copre, anche come sportello (463).
  const coperte = new Set(incarichi.flatMap(sediDellIncarico))
  const anagrafica = (io.sedi ?? []).filter((s) => !sediIncarichi.length || coperte.has(s.id))
  return anagrafica[0]?.id ?? sediIncarichi[0] ?? ''
}

function scelteIniziali(d?: Dettaglio): Scelte {
  if (!d) return { ente: null, mediatore: null, persona: null }
  return {
    // Senza ente il modulo parte senza ente scelto: la riga resta un servizio interno finche'
    // qualcuno non ne indica uno.
    ente: d.stakeholder_id && d.ente ? { id: d.stakeholder_id, nome: d.ente, sigla: d.ente_sigla } : null,
    mediatore: d.mediatore_id
      ? { id: d.mediatore_id, nome: d.mediatore_nome ?? '', cognome: d.mediatore_cognome ?? '' } : null,
    persona: d.persona
      ? { id: d.persona.id, etichetta: `${d.persona.etichetta} · scheda n. ${d.persona.numero_scheda}` }
      : d.con_persona ? undefined : null,
  }
}

/** Corpo della richiesta. Le chiavi con valore `undefined` non partono. */
function corpoDa(v: ValoriModulo, { ente, mediatore, persona }: Scelte, dati: Record<string, unknown>) {
  return {
    // N-2: senza ente la riga vale come servizio interno a CIDIS, con la sola sede. Parte `null` e
    // non `undefined`, altrimenti togliere l'ente da una riga esistente non lo toglierebbe.
    stakeholder_id: ente?.id ?? null,
    referente_id: v.referente_id || null,
    articolazione_id: v.articolazione_id || null,
    progetto_id: v.progetto_id || null,
    sede_id: v.sede_id || null,
    tipologia_codice: v.tipologia_codice,
    sottotipologia_codice: v.sottotipologia_codice || null,
    tema_codice: v.tema_codice || null,
    data: v.data,
    ore: v.ore === '' ? null : Number(v.ore),
    modalita: v.modalita || null,
    luogo: v.luogo.trim() || null,
    mediatore_id: mediatore?.id ?? null,
    lingua: v.lingua || null,
    paese: v.paese || null,
    persona_id: persona === undefined ? undefined : persona?.id ?? null,
    descrizione: v.descrizione.trim() || null,
    soddisfazione: v.soddisfazione || null,
    attestato: v.attestato === '' ? null : v.attestato === 'si',
    dati,
    da_verificare: v.da_verificare,
    motivo_verifica: v.da_verificare ? v.motivo_verifica.trim() || null : undefined,
  } as Record<string, unknown>
}

/** Etichette dei campi del modulo, per gli errori che l'API restituisce con il codice del campo. */
const ETICHETTE_SERVIZIO: Etichette = {
  stakeholder_id: 'Ente', referente_id: 'Referente dell’ente',
  articolazione_id: 'Dipartimento, settore o filiale', tipologia_codice: 'Tipologia',
  sottotipologia_codice: 'Sottotipologia', tema_codice: 'Tema', data: 'Data', ore: 'Ore', modalita: 'Modalità',
  luogo: 'Luogo', sede_id: 'Sede CIDIS', progetto_id: 'Progetto', mediatore_id: 'Mediatore', lingua: 'Lingua',
  paese: 'Paese', persona_id: 'Beneficiario', soddisfazione: 'Soddisfazione dell’ente', attestato: 'Attestato',
  descrizione: 'Descrizione', dati: 'Campi della tipologia', da_verificare: 'Da verificare',
  motivo_verifica: 'Motivo della verifica',
}

/** Errori dell'API senza dettagli, riconosciuti dal codice e portati sul campo a cui si riferiscono. */
const CODICI_CAMPO_SERVIZIO: Record<string, string> = {
  SEDE_MANCANTE: 'sede_id', ORE_MEDIAZIONE: 'ore', MEDIATORE_MANCANTE: 'mediatore_id',
  MOTIVO_VERIFICA: 'motivo_verifica', MOTIVO_DA_RISCRIVERE: 'motivo_verifica', DATA_FUTURA: 'data',
  REFERENTE: 'referente_id', ARTICOLAZIONE: 'articolazione_id',
  SOTTOTIPOLOGIA: 'sottotipologia_codice', TIPOLOGIA: 'tipologia_codice',
  TIPOLOGIA_STORICA: 'tipologia_codice', TIPOLOGIA_NON_AMMESSA: 'tipologia_codice',
  DESCRIZIONE_DA_RISCRIVERE: 'descrizione',
}

function ModuloServizio({ iniziale, salvato, annulla }: {
  iniziale?: Dettaglio; salvato: (id: string) => void; annulla: () => void
}) {
  const { puo, sedi, tassonomia, io } = useSessione()
  const { paesi } = usePaesi()
  const modalita = useCatalogo('modalita_erogazione')
  const soddisfazione = useCatalogo('grado_soddisfazione')
  const lingue = useVoci<Lingua>('/riferimenti/lingue')
  const progetti = useVoci<Progetto>(PERMESSI_PROGETTI.some((p) => puo(p)) ? PROGETTI_SELEZIONABILI : null)

  const [ente, setEnte] = useState<Ente | null>(() => scelteIniziali(iniziale).ente)
  const [mediatore, setMediatore] = useState<Mediatore | null>(() => scelteIniziali(iniziale).mediatore)
  // Sede proposta al servizio nuovo, se la sessione ne indica una fra quelle attive.
  const [proposta] = useState(() => {
    const id = iniziale ? '' : sedeProposta(io)
    return !sedi.length || sedi.some((s) => s.id === id) ? id : ''
  })
  const [v, setV] = useState<ValoriModulo>(() => (iniziale
    ? valoriIniziali(iniziale)
    : { ...valoriIniziali(), sede_id: proposta }))
  const [persona, setPersona] = useState<PersonaScelta | null>(() => scelteIniziali(iniziale).persona ?? null)
  const [dati, setDati] = useState<Record<string, unknown>>(iniziale?.dati ?? {})
  const [referenti, setReferenti] = useState<Referente[]>([])
  const [articolazioni, setArticolazioni] = useState<Articolazione[]>([])
  // Aggiunta al volo di un dipartimento: chi registra il servizio ha l'ente davanti e sa da dove
  // e' arrivata la chiamata; mandarlo nella scheda dell'ente gli farebbe perdere il modulo.
  const [nuovaArticolazione, setNuovaArticolazione] = useState('')
  const [erroreArticolazione, setErroreArticolazione] = useState<string | null>(null)
  const [aggiungendo, setAggiungendo] = useState(false)

  async function aggiungiArticolazione() {
    const nome = nuovaArticolazione.trim()
    if (!ente || !nome) return
    setErroreArticolazione(null)
    setAggiungendo(true)
    try {
      const r = await api.post<{ id: string }>(`/enti/${ente.id}/articolazioni`, { nome })
      setArticolazioni((a) => [...a, { id: r.id, nome, nota: null, attiva: true, servizi: 0 }])
      setV((attuali) => ({ ...attuali, articolazione_id: r.id }))
      setNuovaArticolazione('')
    } catch (e) {
      setErroreArticolazione(messaggioDi(e, 'Dipartimento non aggiunto.'))
    } finally {
      setAggiungendo(false)
    }
  }
  // Aggiunta al volo di un referente, come per i dipartimenti (richiesta della Direzione del
  // 25/10/2026, «Sugli ENTI»): nome, funzione, telefono ed email di chi ha chiamato.
  const [nuovoReferente, setNuovoReferente] = useState<NuovoReferente | null>(null)
  const [erroriReferente, setErroriReferente] = useState<Record<string, string>>({})
  const [erroreReferente, setErroreReferente] = useState<string | null>(null)
  const [aggiungendoReferente, setAggiungendoReferente] = useState(false)
  const scriviReferente = (chiave: keyof NuovoReferente, valore: string) =>
    setNuovoReferente((a) => (a ? { ...a, [chiave]: valore } : a))

  async function aggiungiReferente() {
    const r = nuovoReferente
    if (!ente || !r) return
    const e: Record<string, string> = {}
    if (!r.nome.trim()) e.nome = 'Indicare il nome.'
    if (!r.cognome.trim()) e.cognome = 'Indicare il cognome.'
    if (!emailAccettabile(r.email)) e.email = 'Indirizzo email non valido.'
    if (!telefonoAccettabile(r.telefono)) e.telefono = AIUTO_TELEFONO
    setErroriReferente(e)
    setErroreReferente(null)
    if (Object.keys(e).length) return
    setAggiungendoReferente(true)
    try {
      const nuovo = await api.post<{ id: string }>(`/enti/${ente.id}/referenti`, {
        nome: r.nome.trim(), cognome: r.cognome.trim(), ruolo: r.ruolo.trim() || null,
        email: r.email.trim() || null, telefono: r.telefono.trim() || null, rappresentante_legale: false,
      })
      setReferenti((a) => [...a, { id: nuovo.id, nome: r.nome.trim(), cognome: r.cognome.trim(), attivo: true }])
      setV((attuali) => ({ ...attuali, referente_id: nuovo.id }))
      setNuovoReferente(null)
    } catch (err) {
      setErroreReferente(messaggioDi(err, 'Referente non aggiunto.'))
      setErroriReferente(erroriDellApi(err, {}))
    } finally {
      setAggiungendoReferente(false)
    }
  }
  const [errore, setErrore] = useState<string | null>(null)
  const [errori, setErrori] = useState<Record<string, string>>({})
  const [inCorso, setInCorso] = useState(false)
  // Ogni invio non riuscito porta il fuoco sul primo campo segnalato: il modulo e' lungo e il comando
  // sta in fondo alla finestra.
  const [tentativo, setTentativo] = useState(0)
  const radice = useRef<HTMLFormElement>(null)
  useFuocoSulPrimoErrore(radice, tentativo)

  const imposta = <K extends keyof ValoriModulo>(chiave: K, valore: ValoriModulo[K]) =>
    setV((attuali) => ({ ...attuali, [chiave]: valore }))

  useEffect(() => {
    if (!ente) { setReferenti([]); setArticolazioni([]); return }
    let valido = true
    // GET /enti/:id restituisce a parte il rappresentante legale attivo, che resta un referente
    // dell'ente: si sceglie anche lui, come accetta POST /servizi-erogati.
    api.get<{ referenti: Referente[]; rappresentante_legale: Referente | null
              articolazioni: Articolazione[] }>(`/enti/${ente.id}`)
      .then((r) => {
        if (!valido) return
        setReferenti([...(r.rappresentante_legale ? [r.rappresentante_legale] : []), ...r.referenti])
        setArticolazioni(r.articolazioni ?? [])
      })
      .catch(() => { if (valido) { setReferenti([]); setArticolazioni([]) } })
    return () => { valido = false }
  }, [ente])

  // Modulo della tipologia: lo stesso componente e lo stesso caricamento degli interventi.
  const schema = usaModulo(v.tipologia_codice)
  const etichette = useMemo<Etichette>(() => ({
    ...ETICHETTE_SERVIZIO, ...Object.fromEntries((schema?.campi ?? []).map((c) => [c.codice, c.etichetta])),
  }), [schema])

  const tipologie = (tassonomia?.tipologie ?? []).filter((t) => t.gestione === GESTIONE_ENTI
    && ((t.attiva && !t.storica) || t.codice === iniziale?.tipologia_codice))
  const sottotipologie = (tassonomia?.sottotipologie ?? [])
    .filter((s) => s.tipologia_codice === v.tipologia_codice && s.attiva && !s.storica)
  const temi = (tassonomia?.temi ?? []).filter((t) => t.attivo)
  const mediazione = v.tipologia_codice === MEDIAZIONE
  // Senza il perimetro sulla persona non se ne conosce l'identificativo e la descrizione non si
  // legge: ne' l'una ne' l'altra si possono cambiare.
  const personaBloccata = !!iniziale?.con_persona && !iniziale.persona_visibile
  // Beneficiario tolto in modifica: descrizione e motivo cifrati con la sua chiave non passano in chiaro.
  const staccaBeneficiario = !!iniziale?.persona && !persona

  /**
   * Cambio del beneficiario. Togliendo quello registrato, descrizione e motivo della verifica scritti
   * sotto la sua chiave escono dai campi: senza beneficiario si salverebbero in chiaro, e vanno
   * riscritti senza dati personali. Tornando allo stesso beneficiario con i campi ancora vuoti i
   * testi si ripristinano.
   */
  function scegliPersona(p: PersonaScelta | null) {
    setPersona(p)
    if (!iniziale?.persona) return
    const descrizione = iniziale.descrizione ?? ''
    const motivo = iniziale.motivo_verifica ?? ''
    if (!p) {
      setV((a) => ({ ...a, descrizione: descrizione ? '' : a.descrizione, motivo_verifica: motivo ? '' : a.motivo_verifica }))
    } else if (p.id === iniziale.persona.id) {
      setV((a) => ({ ...a, descrizione: a.descrizione || descrizione, motivo_verifica: a.motivo_verifica || motivo }))
    }
  }

  /**
   * Controlli prima dell'invio, con le regole dell'API. Sulla modifica valgono solo per i campi
   * cambiati e per la chiusura della verifica: una riga migrata con valori che oggi non si
   * accetterebbero si corregge un campo per volta.
   */
  function convalida(): Record<string, string> {
    const e: Record<string, string> = {}
    const prima = iniziale ? valoriIniziali(iniziale) : null
    const tocca = (chiave: keyof ValoriModulo) => !prima || v[chiave] !== prima[chiave]
    const chiudeVerifica = !!prima?.da_verificare && !v.da_verificare
    // L'ente e' facoltativo dalla decisione N-2: senza, il servizio e' interno a CIDIS e lo
    // identifica la sede, che questo modulo chiede comunque.
    if (!ente && !v.sede_id) {
      e.stakeholder_id = 'Indicare l’ente a cui è stato reso il servizio, oppure la sede che lo ha erogato.'
    }
    if (!v.tipologia_codice) e.tipologia_codice = 'Scegliere la tipologia.'
    if (tocca('data') && !v.data) e.data = 'Indicare la data del servizio.'
    else if (tocca('data') && v.data > oggi()) e.data = 'La data di un servizio erogato non può essere futura.'
    if ((tocca('sede_id') || chiudeVerifica) && !v.sede_id) e.sede_id = 'Indicare la sede che ha erogato il servizio.'
    if (mediazione) {
      // Multipli di mezz'ora da 0,5 a 10 (decisione del 16/09/2026): il confronto sta sui mezzi,
      // perche' 1,5 in virgola mobile non e' esatto mentre 1,5 x 2 = 3 lo e'.
      const oreScritte = v.ore.trim().replace(',', '.')
      const oreValide = oreScritte !== '' && Number.isInteger(Number(oreScritte) * 2)
        && Number(oreScritte) >= 0.5 && Number(oreScritte) <= 10
      if ((tocca('ore') || tocca('tipologia_codice') || chiudeVerifica) && !oreValide) {
        e.ore = 'Le ore di mediazione sono mezz’ora o multipli, da 0,5 a 10.'
      }
      const mediatoreCambiato = !prima || (mediatore?.id ?? null) !== (iniziale?.mediatore_id ?? null)
      if ((mediatoreCambiato || tocca('tipologia_codice')) && !mediatore) e.mediatore_id = 'Per una mediazione serve il mediatore.'
    } else if (tocca('ore') && v.ore.trim() && !(Number(v.ore) > 0)) {
      e.ore = 'Le ore sono un numero maggiore di zero.'
    }
    if (v.da_verificare && (tocca('motivo_verifica') || tocca('da_verificare')) && v.motivo_verifica.trim().length < 3) {
      e.motivo_verifica = 'Indicare perché il servizio è da verificare, in almeno tre caratteri.'
    }
    return e
  }

  async function salva() {
    setErrore(null)
    const trovati = convalida()
    if (Object.keys(trovati).length) {
      setErrori(trovati)
      setErrore('Correggere i campi segnalati.')
      setTentativo((n) => n + 1)
      return
    }
    setErrori({})
    let corpo = corpoDa(v, { ente, mediatore, persona: personaBloccata ? undefined : persona }, dati)
    if (iniziale) {
      // Sulla modifica partono solo i campi cambiati: l'API controlla quello che si tocca, e un
      // valore migrato che oggi non si accetterebbe non deve bloccare la correzione di un altro.
      const prima = corpoDa(valoriIniziali(iniziale), scelteIniziali(iniziale), iniziale.dati)
      corpo = Object.fromEntries(Object.entries(corpo).filter(([chiave, valore]) =>
        valore !== undefined && JSON.stringify(valore) !== JSON.stringify(prima[chiave])))
      // Staccando il beneficiario l'API vuole sapere che cosa resta dei testi cifrati con la sua
      // chiave: parte il contenuto dei campi, anche vuoto. Il motivo di una verifica conclusa non ha
      // un campo a video e non si conserva.
      if (staccaBeneficiario) {
        corpo.descrizione = v.descrizione.trim() || null
        if (iniziale.motivo_verifica) corpo.motivo_verifica = v.da_verificare ? v.motivo_verifica.trim() || null : null
      }
      if (!Object.keys(corpo).length) { salvato(iniziale.id); return }
    }
    setInCorso(true)
    try {
      if (iniziale) {
        await api.patch(`/servizi-erogati/${iniziale.id}`, corpo)
        salvato(iniziale.id)
      } else {
        const creato = await api.post<{ id: string }>('/servizi-erogati', corpo)
        salvato(creato.id)
      }
    } catch (e) {
      // Sede, area, progetto o ente fuori dagli incarichi hanno un messaggio proprio (400): il 404
      // resta per il beneficiario che l'utente non vede e, in modifica, per la riga.
      setErrore(messaggioDi(e, iniziale
        ? 'Con i tuoi incarichi non puoi salvarlo: controlla sede, progetto, tipologia e beneficiario.'
        : 'Il beneficiario scelto non rientra nei tuoi incarichi: toglilo, oppure chiedi a un collega che lo segue di registrare il servizio.'))
      setErrori(erroriDellApi(e, CODICI_CAMPO_SERVIZIO))
      setTentativo((n) => n + 1)
    } finally {
      setInCorso(false)
    }
  }

  return (
    <form ref={radice} onSubmit={(e) => { e.preventDefault(); void salva() }} noValidate>
      <h3 className="rete-titoletto">Ente e servizio</h3>
      <div className="griglia-modulo">
        <SceltaEnte etichetta="Ente" vuota="— nessuno: servizio interno a CIDIS —" valore={ente}
                    errore={errori.stakeholder_id}
                    cambia={(e) => { setEnte(e); imposta('referente_id', ''); setNuovoReferente(null) }} />
        <Scelta etichetta="Referente dell’ente" vuota="— nessuno —" valore={v.referente_id}
                cambia={(x) => imposta('referente_id', x)} disabilitato={!ente} errore={errori.referente_id}
                opzioni={conValore(referenti.filter((r) => r.attivo).map((r) => ({
                  valore: r.id, etichetta: `${r.nome} ${r.cognome}${r.rappresentante_legale ? ' · rappresentante legale' : ''}` })),
                  iniziale?.referente_id, nomeCompleto(iniziale?.referente_nome ?? null, iniziale?.referente_cognome ?? null))}
                aiuto={ente && puo('ente.gestisci') ? undefined : 'Si aggiunge dalla scheda dell’ente.'} />
        {ente && puo('ente.gestisci') && !nuovoReferente && (
          <div className="rete-voce-modulo">
            <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />}
                     onClick={() => { setNuovoReferente({ ...REFERENTE_VUOTO }); setErroriReferente({}); setErroreReferente(null) }}>
              Aggiungi un referente
            </Bottone>
          </div>
        )}
        {ente && nuovoReferente && (
          <fieldset className="gruppo-caselle rete-referente-volo">
            <legend>Nuovo referente di {ente.nome}</legend>
            <div className="griglia-modulo">
              <Campo etichetta="Nome" obbligatorio maxLength={100} valore={nuovoReferente.nome}
                     errore={erroriReferente.nome} cambia={(x) => scriviReferente('nome', x)} />
              <Campo etichetta="Cognome" obbligatorio maxLength={100} valore={nuovoReferente.cognome}
                     errore={erroriReferente.cognome} cambia={(x) => scriviReferente('cognome', x)} />
              <Campo etichetta="Funzione" maxLength={120} valore={nuovoReferente.ruolo}
                     errore={erroriReferente.ruolo} cambia={(x) => scriviReferente('ruolo', x)} />
              <Campo etichetta="Telefono" tipo="tel" maxLength={40} segnaposto="+39 333 1234567"
                     valore={nuovoReferente.telefono} errore={erroriReferente.telefono}
                     cambia={(x) => scriviReferente('telefono', x)} />
              <Campo etichetta="Email" tipo="email" maxLength={200} valore={nuovoReferente.email}
                     errore={erroriReferente.email} cambia={(x) => scriviReferente('email', x)} />
            </div>
            {erroreReferente && <p className="campo-errore" role="alert">{erroreReferente}</p>}
            <div className="riga">
              <Bottone variante="secondario" dimensione="piccolo" inCorso={aggiungendoReferente}
                       onClick={() => void aggiungiReferente()}>Aggiungi il referente</Bottone>
              <Bottone variante="discreto" dimensione="piccolo" onClick={() => setNuovoReferente(null)}>Annulla</Bottone>
            </div>
          </fieldset>
        )}
        {/* Osservazioni 3, punto 9: lo stesso ente chiama da piu' dipartimenti, e il dipartimento
            scritto una volta si ritrova alla chiamata successiva. */}
        <Scelta etichetta="Dipartimento, settore o filiale" vuota="— non indicato —" valore={v.articolazione_id}
                cambia={(x) => imposta('articolazione_id', x)} disabilitato={!ente}
                errore={errori.articolazione_id}
                opzioni={conValore(articolazioni.filter((a) => a.attiva).map((a) => ({ valore: a.id, etichetta: a.nome })),
                  iniziale?.articolazione_id, iniziale?.articolazione ?? null)}
                aiuto={ente && puo('ente.gestisci') ? undefined : 'Si aggiunge dalla scheda dell’ente.'} />
        {ente && puo('ente.gestisci') && (
          <div className="rete-voce-modulo">
            <Campo etichetta="Aggiungi un dipartimento" maxLength={200} valore={nuovaArticolazione}
                   cambia={setNuovaArticolazione} errore={erroreArticolazione}
                   segnaposto="Pronto Soccorso, Caritas…" />
            <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />}
                     disabled={!nuovaArticolazione.trim()} inCorso={aggiungendo}
                     onClick={() => void aggiungiArticolazione()}>
              Aggiungi
            </Bottone>
          </div>
        )}
        <Scelta etichetta="Tipologia" obbligatorio vuota="— seleziona —" valore={v.tipologia_codice}
                errore={errori.tipologia_codice}
                cambia={(x) => { setV((a) => ({ ...a, tipologia_codice: x, sottotipologia_codice: '' })); setDati({}) }}
                opzioni={tipologie.map((t) => ({ valore: t.codice, etichetta: t.storica ? `${t.nome} (storica)` : t.nome }))} />
        {sottotipologie.length > 0 && (
          <Scelta etichetta="Sottotipologia" vuota="— nessuna —" valore={v.sottotipologia_codice}
                  cambia={(x) => imposta('sottotipologia_codice', x)} errore={errori.sottotipologia_codice}
                  opzioni={sottotipologie.map((s) => ({ valore: s.codice, etichetta: s.nome }))} />
        )}
        <Scelta etichetta="Tema" vuota="— nessuno —" valore={v.tema_codice} cambia={(x) => imposta('tema_codice', x)}
                errore={errori.tema_codice}
                opzioni={conValore(temi.map((t) => ({ valore: t.codice, etichetta: t.nome })), iniziale?.tema_codice, iniziale?.tema)} />
      </div>

      <h3 className="rete-titoletto">Quando, dove, per quale progetto</h3>
      <div className="griglia-modulo">
        <Campo etichetta="Data" tipo="date" obbligatorio max={oggi()} valore={v.data} cambia={(x) => imposta('data', x)}
               errore={errori.data} />
        {mediazione
          ? <Campo etichetta="Ore di mediazione" tipo="number" obbligatorio min={0.5} max={10} passi={0.5}
                   modalitaTastiera="decimal" valore={v.ore} cambia={(x) => imposta('ore', x)}
                   errore={errori.ore} aiuto="Mezz’ora o multipli, da 0,5 a 10." />
          : <Campo etichetta="Ore" tipo="number" min={0.25} passi={0.25} valore={v.ore}
                   cambia={(x) => imposta('ore', x)} errore={errori.ore}
                   aiuto="Durata del servizio, facoltativa: almeno un quarto d’ora." />}
        <Scelta etichetta="Modalità" vuota="— seleziona —" valore={v.modalita} cambia={(x) => imposta('modalita', x)}
                errore={errori.modalita}
                opzioni={conValore(modalita.voci.map((m) => ({ valore: m.codice, etichetta: m.nome })),
                  iniziale?.modalita, iniziale?.modalita_nome)} />
        <Campo etichetta="Luogo" maxLength={300} valore={v.luogo} cambia={(x) => imposta('luogo', x)}
               errore={errori.luogo} segnaposto="Per esempio: ufficio anagrafe del Comune" />
        <Scelta etichetta="Sede CIDIS" obbligatorio vuota="— seleziona —" valore={v.sede_id}
                cambia={(x) => imposta('sede_id', x)} errore={errori.sede_id}
                aiuto={proposta ? 'Proposta la tua sede: cambiala se il servizio l’ha reso un’altra sede.' : undefined}
                opzioni={conValore(opzioniSedi(sedi), iniziale?.sede_id, iniziale?.sede)} />
        <Scelta etichetta="Progetto" vuota="— nessun progetto —" valore={v.progetto_id}
                cambia={(x) => imposta('progetto_id', x)} errore={errori.progetto_id}
                opzioni={conValore(progetti.map((p) => ({
                  valore: p.id, etichetta: p.codice_cidis ? `${p.codice_cidis} · ${p.titolo}` : p.titolo })),
                  iniziale?.progetto_id, iniziale?.progetto)} />
      </div>

      <h3 className="rete-titoletto">Mediazione</h3>
      <div className="griglia-modulo">
        <SceltaMediatore valore={mediatore} cambia={setMediatore} obbligatorio={mediazione} errore={errori.mediatore_id} />
        {/* Con le voci in piu' delle lingue (553): «Persiano (farsi)» sotto la P, e si salva fas. */}
        <Scelta etichetta="Lingua" vuota="— nessuna —" valore={v.lingua} cambia={(x) => imposta('lingua', codiceDaVoce(x))}
                errore={errori.lingua}
                opzioni={conValore(opzioniConAltreVoci(lingueDalCatalogo(lingue)),
                  iniziale?.lingua, iniziale?.lingua_nome)} />
        <Scelta etichetta="Paese" vuota="— nessuno —" valore={v.paese} cambia={(x) => imposta('paese', x)}
                errore={errori.paese}
                opzioni={conValore(paesi.map((p) => ({ valore: p.codice_iso3, etichetta: p.nome })),
                  iniziale?.paese, iniziale?.paese_nome)} />
      </div>

      <h3 className="rete-titoletto">Destinatario ed esito</h3>
      <div className="griglia-modulo">
        {personaBloccata
          ? <Campo etichetta="Beneficiario" valore="Collegato, ma non rientra nei tuoi incarichi" disabilitato
                   cambia={() => undefined}
                   aiuto="Il beneficiario si cambia solo se la sua scheda rientra nei tuoi incarichi." />
          : puo('persona.cerca') && <SceltaPersona valore={persona} cambia={scegliPersona} errore={errori.persona_id} />}
        <Scelta etichetta="Soddisfazione dell’ente" vuota="— non rilevata —" valore={v.soddisfazione}
                cambia={(x) => imposta('soddisfazione', x)} errore={errori.soddisfazione}
                opzioni={conValore(soddisfazione.voci.map((s) => ({ valore: s.codice, etichetta: s.nome })),
                  iniziale?.soddisfazione, iniziale?.soddisfazione_nome)} />
        <Scelta etichetta="Attestato" vuota="— non indicato —" valore={v.attestato} errore={errori.attestato}
                cambia={(x) => imposta('attestato', x as ValoriModulo['attestato'])}
                opzioni={[{ valore: 'si', etichetta: 'Rilasciato' }, { valore: 'no', etichetta: 'Non rilasciato' }]} />
      </div>
      <AreaTesto etichetta="Descrizione" righe={4} maxLength={4000} valore={v.descrizione}
                 cambia={(x) => imposta('descrizione', x)} disabilitato={personaBloccata} errore={errori.descrizione}
                 aiuto={personaBloccata
                   ? 'La descrizione riguarda un beneficiario che non rientra nei tuoi incarichi: non si può leggere.'
                   : persona
                     ? 'Cifrata con la chiave del beneficiario.'
                     : staccaBeneficiario && iniziale?.descrizione
                       ? 'La descrizione cifrata con la chiave del beneficiario non si conserva senza di lui: se serve, riscriverla senza dati personali.'
                       : 'Senza beneficiario la descrizione non è cifrata: non inserire dati personali.'} />

      {schema && schema.campi.length > 0 && (
        <>
          <h3 className="rete-titoletto">Campi della tipologia</h3>
          <div className="griglia-modulo">
            {schema.campi.map((c) => (
              <CampoModulo key={c.codice} campo={c} valore={dati[c.codice]} errore={errori[c.codice]}
                           cambia={(x) => setDati((attuali) => ({ ...attuali, [c.codice]: x }))} />
            ))}
          </div>
        </>
      )}

      <h3 className="rete-titoletto">Verifica</h3>
      <Spunta etichetta="Da verificare" valore={v.da_verificare} cambia={(x) => imposta('da_verificare', x)}
              aiuto={staccaBeneficiario && iniziale?.motivo_verifica && !v.da_verificare
                ? 'Il motivo della verifica, cifrato con la chiave del beneficiario, non si conserva senza di lui.'
                : iniziale?.da_verificare
                  ? 'Togliere la spunta conclude la verifica: servono la sede e, per la mediazione, le ore corrette.'
                  : 'Per un servizio con dati ancora da confermare.'} />
      {v.da_verificare && (
        <Campo etichetta="Motivo della verifica" obbligatorio maxLength={500} valore={v.motivo_verifica}
               cambia={(x) => imposta('motivo_verifica', x)} errore={errori.motivo_verifica}
               aiuto={iniziale?.da_verificare && iniziale.motivo_verifica === null
                 ? 'Il motivo registrato non si può leggere: il servizio riguarda un beneficiario che non rientra nei tuoi incarichi. Se lasci il campo vuoto, resta com’è.'
                 : persona ? 'Cifrato con la chiave del beneficiario.' : 'Almeno tre caratteri.'} />
      )}

      <AvvisoModulo errore={errore} errori={errori} etichette={etichette} />
      <div className="azioni-modulo">
        <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
        <Bottone variante="primario" tipo="submit" inCorso={inCorso}>
          {iniziale ? 'Salva le modifiche' : 'Registra il servizio'}
        </Bottone>
      </div>
    </form>
  )
}
